import { GENDER_FILTERS, RADIUS_KM, SCOPES, type SearchPrefs } from '@repo/shared/domain';
import {
  PLANS,
  canUseGenderFilter,
  canUseScope,
  requiredPlan,
  type PlanId,
} from '@repo/shared/plans';
import { GENDER_FILTER_LABELS, SCOPE_LABELS } from '../labels.ts';
import { Icon } from './Icon.tsx';
import { Modal } from './Modal.tsx';

interface FilterSheetProps {
  prefs: SearchPrefs;
  plan: PlanId;
  countryLabel: string | null;
  locating: boolean;
  locationProblem: string | null;
  onChange: (prefs: SearchPrefs) => void;
  /** Gesperrte Option angetippt → Tarife zeigen */
  onLocked: () => void;
  onClose: () => void;
}

export function FilterSheet({
  prefs,
  plan,
  countryLabel,
  locating,
  locationProblem,
  onChange,
  onLocked,
  onClose,
}: FilterSheetProps) {
  const genderLocked = !canUseGenderFilter(plan);

  return (
    <Modal title="Wen möchtest du treffen?" onClose={onClose} sheet>
      <section className="filter-group">
        <h3>
          <Icon name="globe" size={18} /> Region
        </h3>
        <div className="options">
          {SCOPES.map((scope) => {
            const locked = !canUseScope(plan, scope);
            return (
              <button
                key={scope}
                type="button"
                className="option"
                aria-pressed={prefs.scope === scope}
                onClick={() => (locked ? onLocked() : onChange({ ...prefs, scope }))}
              >
                {SCOPE_LABELS[scope]}
                {scope === 'national' && countryLabel && <small>{countryLabel}</small>}
                {scope === 'local' && !locked && (
                  <small>{locating ? 'Standort …' : `${prefs.radiusKm} km`}</small>
                )}
                {locked && (
                  <span className="option__lock">
                    <Icon name="lock" size={12} /> {PLANS[requiredPlan(scope, false)].name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {prefs.scope === 'local' && (
          <label className="range">
            <span>
              Umkreis <strong>{prefs.radiusKm} km</strong>
            </span>
            <input
              type="range"
              min={RADIUS_KM.min}
              max={RADIUS_KM.max}
              step={RADIUS_KM.step}
              value={prefs.radiusKm}
              onChange={(event) => onChange({ ...prefs, radiusKm: Number(event.target.value) })}
            />
          </label>
        )}
        {locationProblem && prefs.scope !== 'local' && <p className="alert">{locationProblem}</p>}
      </section>

      <section className="filter-group">
        <h3>
          <Icon name="users" size={18} /> Geschlecht
          {genderLocked && <span className="pill">Premium</span>}
        </h3>
        <div className="options options--4">
          {GENDER_FILTERS.map((filter) => {
            const locked = filter !== 'any' && genderLocked;
            return (
              <button
                key={filter}
                type="button"
                className="option"
                aria-pressed={prefs.genderFilter === filter}
                onClick={() => (locked ? onLocked() : onChange({ ...prefs, genderFilter: filter }))}
              >
                {GENDER_FILTER_LABELS[filter]}
                {locked && (
                  <span className="option__lock">
                    <Icon name="lock" size={12} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <p className="hint">Gilt sofort. Läuft gerade ein Gespräch, dann ab der nächsten Person.</p>
      <button type="button" className="button button--primary" onClick={onClose}>
        Fertig
      </button>
    </Modal>
  );
}
